<template>
  <panel-layout
    id="tax-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="tax" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ tax.isNewRecord ? $t('tax.new') : tax.name }}
          </h2>
          <div v-if="!tax.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.tax.edit.id', { id: tax.idTax })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="tax" ref="form" class="h-full w-full">
      <o-scroll>
        <div class="w-full px-6 py-4">
          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')">
              <text-field v-model="tax.name" :label="$t('general.title')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group
              v-slot="{ status }"
              rules="between:0,100"
              class="w-full"
              :label="$t('general.amount')"
              :name="$t('general.amount')"
              :info="$t('pages.tax.edit.amountInfo')"
            >
              <number-field
                v-model="tax.amount"
                :min="0"
                :max="100"
                slim
                :status="status"
                trailing="%"
              />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group col reverse class="w-full md:w-1/2" :label="$t('pages.tax.edit.displayPrice')" :info="$t('pages.tax.edit.displayPriceInfo')">
              <toggle-field v-model="tax.includeInDisplayPrice" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group col reverse class="w-full md:w-1/2" :label="$t('pages.tax.edit.default')" :info="$t('pages.tax.edit.defaultInfo')">
              <toggle-field v-model="tax.includeByDefault" />
            </o-field-group>
          </div>

          <template v-if="!tax.isNewRecord">
            <div class="mt-16 mb-4">
              <h4 class="text-gray-700 font-medium text-sm dark:text-gray-200">
                {{ $t('tax.bulkSet') }}
              </h4>
              <p class="text-sm text-gray-600 dark:text-gray-300">
                {{ $t('pages.tax.edit.apply', { name: tax.name }) }}
              </p>
            </div>

            <div class="w-full">
              <div class="flex items-center justify-between mb-2 py-2 border-b w-full">
                <div>
                  <label class="text-sm block leading-none text-gray-700 font-medium dark:text-gray-200">{{ $t('general.services') }}</label>
                  <span class="text-sm text-gray-600 dark:text-gray-300">
                    {{ $t('pages.tax.edit.overrideCustomService') }}
                  </span>
                </div>

                <div>
                  <o-button size="sm" variant="success" @click="handleBulkSetTax('services', true)">
                    {{ $t('general.enable') }}
                  </o-button>
                  <o-button size="sm" variant="danger" @click="handleBulkSetTax('services', false)">
                    {{ $t('general.disable') }}
                  </o-button>
                </div>
              </div>

              <div class="flex items-center justify-between mb-2 py-2 border-b w-full">
                <div>
                  <label class="text-sm block leading-none text-gray-700 font-medium dark:text-gray-200">{{ $t('general.products') }}</label>
                  <span class="text-sm text-gray-600 dark:text-gray-300">
                    {{ $t('pages.tax.edit.overrideCustomProduce') }}
                  </span>
                </div>

                <div>
                  <o-button size="sm" variant="success" @click="handleBulkSetTax('products', true)">
                    {{ $t('general.enable') }}
                  </o-button>
                  <o-button size="sm" variant="danger" @click="handleBulkSetTax('products', false)">
                    {{ $t('general.disable') }}
                  </o-button>
                </div>
              </div>

              <div class="flex items-center justify-between mb-2 py-2 border-b w-full">
                <div>
                  <label class="text-sm block leading-none text-gray-700 font-medium dark:text-gray-200">{{ $t('general.passes') }}</label>
                  <span class="text-sm text-gray-600 dark:text-gray-300">
                    {{ $t('pages.tax.edit.overrideCustomPass') }}
                  </span>
                </div>

                <div>
                  <o-button size="sm" variant="success" @click="handleBulkSetTax('passes', true)">
                    {{ $t('general.enable') }}
                  </o-button>
                  <o-button size="sm" variant="danger" @click="handleBulkSetTax('passes', false)">
                    {{ $t('general.disable') }}
                  </o-button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="tax">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <bulk-set-dialog ref="bulkSetDialog" :portal="panel.portal" />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import BulkSetDialog from '@/components/tax/actions/BulkSet.vue'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  TextField,
  NumberField,
  ToggleField,
} from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const tax = ref<any>(null)
const errors = ref<any>(false)
const loading = ref(true)

const confirmDialog = ref<any>()
const bulkSetDialog = ref<any>()
const form = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(tax)

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const _tax = (await $api.tax.save(tax.value)).data

    if (_tax) {
      tax.value = _tax
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.tax.edit.success'),
        text: $i18n.t('pages.tax.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Tax', _tax)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    console.log(e.errors)
    errors.value = e?.errors

    $notification.error({ errors: e?.errors })

    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleBulkSetTax = (type: string, state: boolean) => {
  bulkSetDialog.value.open(tax.value, type, state)
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      tax.value = (await $api.tax.defaults({} as any)).data
    } else {
      tax.value = (await $api.tax.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
